import React, { useEffect, useState } from "react";
import { api, formatApiError, todayStr } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LineItemsEditor, emptyLine, linesValid } from "@/components/stock/LineItemsEditor";
import { FormField, WarehouseSelect } from "@/components/stock/FormField";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";

const blank = () => ({ date: todayStr(), from_wh: "central", to_wh: "printing", ref_no: "", note: "", lines: [emptyLine()] });

export const TransferDialog = ({ open, onClose, items, onSaved }) => {
  const { t } = useLang();
  const [form, setForm] = useState(blank());
  useEffect(() => { if (open) setForm(blank()); }, [open]);
  const set = (k) => (v) => setForm({ ...form, [k]: v });

  const save = async () => {
    if (form.from_wh === form.to_wh) return toast.error(t("same_wh_error"));
    if (!linesValid(form.lines)) return toast.error(t("lines_invalid"));
    try {
      const lines = form.lines.map((l) => ({ item_type: l.item_type, item_id: l.item_id, qty: Number(l.qty) }));
      const { data } = await api.post("/stock-transfers", { ...form, lines });
      toast.success(`${t("saved")} · ${data.doc_no}`);
      onClose();
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="transfer-dialog">
        <DialogHeader><DialogTitle>{t("transfer")}</DialogTitle></DialogHeader>
        <div className="flex flex-wrap items-end gap-3">
          <FormField label={t("from_wh")} className="w-44">
            <WarehouseSelect value={form.from_wh} onChange={set("from_wh")} availableOnly testid="transfer-from-select" />
          </FormField>
          <ArrowRight size={18} className="mb-2.5 text-slate-400" />
          <FormField label={t("to_wh")} className="w-44">
            <WarehouseSelect value={form.to_wh} onChange={set("to_wh")} testid="transfer-to-select" />
          </FormField>
          <FormField label={t("date")} className="w-40">
            <Input type="date" value={form.date} onChange={(e) => set("date")(e.target.value)} />
          </FormField>
          <FormField label={t("ref_no")} className="flex-1 min-w-[140px]">
            <Input value={form.ref_no} onChange={(e) => set("ref_no")(e.target.value)} data-testid="transfer-ref-input" />
          </FormField>
        </div>
        <LineItemsEditor lines={form.lines} onChange={set("lines")} items={items} showStock warehouse={form.from_wh} testid="transfer-lines" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("cancel")}</Button>
          <Button onClick={save} className="bg-sky-700 hover:bg-sky-800" data-testid="transfer-save-btn">{t("save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
